import * as React from 'react';
import { Popover } from '@inwink/modals';
import { Entities } from '@inwink/entities/entities';
import { withI18nHelper } from '@inwink/i18n';
import { AppTextLabel } from '@@commons';

export const regex = [
    {
        key: "mail",
        value: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
    },
    {
        key: "text",
        value: /.*/

    },
    {
        key: "number",
        value: /^[0-9]*$/
    }
];

export interface IMultiTextInputRecommandationsItem {
    value: string | string[];
    trad?: string;
}

export interface IMultiTextInputProps {
    i18n: string;
    i18nHelper?: Entities.i18nHelper;
    onChange: (val: any[]) => void;
    value: string | string[];
    canEditTrad: boolean;
    type?: string;
    recommandations?: IMultiTextInputRecommandationsItem[];
    readonly?: boolean;
}

interface IMultiTextInputState {
    currentValue: string;
    values: string[];
    isInvalid: boolean;
    showRecommandations?: boolean;
}

@withI18nHelper()
export class MultiTextInput extends React.Component<IMultiTextInputProps, IMultiTextInputState> {
    inputwrapper = React.createRef<HTMLDivElement>();

    constructor(props: IMultiTextInputProps) {
        super(props);

        this.state = {
            currentValue: "",
            values: Array.isArray(props.value) ? [...props.value] : [],
            isInvalid: false
        };
    }

    componentDidUpdate(prevProps: IMultiTextInputProps) {
        if ((!prevProps.value || !prevProps.value.length) && this.props.value && this.props.value.length) {
            this.setState({
                values: Array.isArray(this.props.value) ? [...this.props.value] : []
            });
        }
    }

    check = (value: string): boolean => {
        if (!this.props.type) return true;
        
        const rule = regex.find(r => r.key === this.props.type)?.value;
        return rule ? rule.test(value) : true;
    };

    removeValue = (arg: React.MouseEvent<HTMLButtonElement>, index: number): void => {
        arg.preventDefault();
        arg.stopPropagation();
        
        const newValues = [...this.state.values];
        newValues.splice(index, 1);
        
        this.setState({ values: newValues }, () => {
            this.props.onChange(newValues);
        });
    };

    addValue = (arg: React.MouseEvent<any> | null, value?: string | string[]): void => {
        arg?.preventDefault();
        arg?.stopPropagation();

        const valueToAdd = value ?? this.state.currentValue;
        
        if (!valueToAdd) return;

        const valueToAddArray = (Array.isArray(valueToAdd) ? valueToAdd : [valueToAdd])
            .map(v => typeof v === 'string' ? v.trim() : v)
            .filter(v => v?.length > 0);

        if (!valueToAddArray?.length) return;

        const uniqueNewValues = valueToAddArray?.filter(newVal => !this.state.values.includes(newVal));

        if (!uniqueNewValues?.length) {
            this.setState({ currentValue: "", isInvalid: false });
            return;
        }

        const isCorrect = this.props.type ? uniqueNewValues.every(v => this.check(v)) : true;
        
        if (isCorrect) {
            const valuesUpdated = [...this.state.values, ...uniqueNewValues];
            
            this.setState({
                values: valuesUpdated,
                currentValue: "",
                isInvalid: false
            }, () => {
                this.props.onChange(valuesUpdated);
            });
        } else {
            this.setState({ isInvalid: true });
        }
    };

    onChange = (val: string): void => {
        this.setState({ currentValue: val });
    };

    onRecommandationsChange = (arg: React.MouseEvent<any>, value: string | string[]): void => {
        this.setState({ showRecommandations: false });
        this.addValue(arg, value);
    };

    getValueLabel = (value: string, i18nHelper?: Entities.i18nHelper): string | undefined => {
        if (!this.props.recommandations?.length || !i18nHelper) return undefined;

        const recommendation = this.props.recommandations.find(rec => rec.value === value || rec.value?.includes(value));
        if (recommendation?.trad) {
            return i18nHelper.translate(recommendation.trad);
        }
    };

    onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.charCode === 13) {
            this.addValue(null);
        }
    };

    render() {
        const { i18nHelper, recommandations, i18n, canEditTrad, type, readonly } = this.props;
        const { values, currentValue, isInvalid, showRecommandations } = this.state;
        
        const hasRecommandations = recommandations && recommandations.length > 0;
        
        return (
            <div className="fieldbloc multitextinput">
                <AppTextLabel component="label" i18n={i18n} />
                <AppTextLabel
                    component="div"
                    className="field-desc"
                    i18n={`${i18n}.desc`}
                    defaultText={canEditTrad ? `${i18n}.desc` : ""} 
                />
                
                <div className="add-field" ref={this.inputwrapper}>
                    <input
                        onKeyPress={this.onKeyPress}
                        onClick={hasRecommandations ? () => this.setState({ showRecommandations: true }) : undefined}
                        type={type || "text"}
                        readOnly={readonly || false}
                        className={isInvalid ? "invalid" : ""}
                        onChange={(arg) => this.onChange(arg.currentTarget.value)}
                        value={currentValue} 
                    />
                    <button onClick={this.addValue} className="add-picto lightbtn">
                        <i className='inwink-add'/>
                    </button>
                </div>
                
                {hasRecommandations && (
                    <Popover
                        className="multitextinput-recommandations-modal"
                        show={showRecommandations || false}
                        parent={this.inputwrapper.current}
                        minWidth={300}
                        onhide={() => this.setState({ showRecommandations: false })}
                        autofocus={false}
                    >
                        <MultiTextInputRecommandationsList
                            onChange={this.onRecommandationsChange}
                            recommandations={recommandations}
                            i18nHelper={i18nHelper} 
                        />
                    </Popover>
                )}
                
                <div className="values">
                    {values.map((value, index) => {
                        const label = this.getValueLabel(value, i18nHelper);
                        const displayValue = Array.isArray(value) ? value.join(", ") : value;
                        return (
                            <div className="value" key={index}>
                                <span>{label ? `${label} (${displayValue})` : displayValue}</span>
                                <button 
                                    onClick={(event) => this.removeValue(event, index)} 
                                    className="btnremove"
                                >
                                    <i className='inwink-cancel'/>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

interface IMultiTextInputRecommandationsListProps {
    recommandations: IMultiTextInputRecommandationsItem[];
    onChange: (arg: React.MouseEvent<any>, res: string | string[]) => void;
    i18nHelper?: Entities.i18nHelper;
}

class MultiTextInputRecommandationsList extends React.Component<IMultiTextInputRecommandationsListProps, any> {
    render() {
        const { recommandations, onChange, i18nHelper } = this.props;
        
        return (
            <ul className="recommandations">
                {recommandations?.map((recommandation, idx) => {
                    const label = recommandation.trad && i18nHelper 
                        ? i18nHelper.translate(recommandation.trad) 
                        : null;
                    
                    const displayRecommandation = Array.isArray(recommandation.value)
                        ? recommandation.value.join(", ")
                        : recommandation.value;

                    return (
                        <li onClick={(arg) => onChange(arg, recommandation.value)} key={idx}>
                            {label ? `${label} (${displayRecommandation})` : displayRecommandation}
                        </li>
                    );
                })}
            </ul>
        );
    }
}